import { useEffect, useRef } from 'react';

// Screen Wake Lock API の Sentinel 型(MDN 準拠)
interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
  removeEventListener(type: 'release', listener: () => void): void;
}

interface NavigatorWithWakeLock {
  wakeLock?: {
    request(type: 'screen'): Promise<WakeLockSentinelLike>;
  };
}

/**
 * Screen Wake Lock API で画面の自動スリープを抑制する。
 * 採寸ページ等、現場で長時間表示しながら片手操作するページで使用。
 *
 * 原則 14(UI 規約)補完:現場でアプリを開きながら作業する画面で
 * 画面がロックされると採寸の紐付け作業が中断される。
 *
 * 注意:
 * - HTTPS 必須(localhost を除く)
 * - 一部ブラウザ(古い iOS Safari)は未サポート → 静かにフォールバック
 * - visibilitychange でタブ切替時に sentinel が自動解放されるため、
 *   タブが visible に戻ったら再取得する
 */
export function useScreenWakeLock(enabled: boolean): void {
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const nav = navigator as unknown as NavigatorWithWakeLock;
    if (!nav.wakeLock) return; // 未サポート環境はスキップ

    let cancelled = false;

    const request = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const sentinel = await nav.wakeLock!.request('screen');
        if (cancelled) {
          await sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
        sentinel.addEventListener('release', () => {
          if (sentinelRef.current === sentinel) sentinelRef.current = null;
        });
      } catch {
        // user gesture 必須等で reject → 諦める(コンソール汚染しない)
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !sentinelRef.current) {
        void request();
      }
    };

    void request();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s && !s.released) {
        void s.release().catch(() => {});
      }
    };
  }, [enabled]);
}
